/**
 * Whether the camera screens are laid out SIDEWAYS — the phone held in landscape on a short screen, where the capture
 * bar becomes the ~116 px rail beside the viewfinder. `SIDEWAYS_QUERY` is the query the stylesheet's rail block uses
 * (src/styles/svapp.css, `@media (orientation: landscape) and (max-height: 500px)`); the two are pinned equal by a test.
 * Live: turning the phone re-renders. A browser with no `matchMedia` reads as upright (as every test environment does).
 */

import { useSyncExternalStore } from 'react';

export const SIDEWAYS_QUERY = '(orientation: landscape) and (max-height: 500px)';

type Legacy = MediaQueryList & {
  addListener?: (l: (e: MediaQueryListEvent) => void) => void;
  removeListener?: (l: (e: MediaQueryListEvent) => void) => void;
};

function query(): Legacy | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia(SIDEWAYS_QUERY) as Legacy;
  } catch {
    return null;
  }
}

function snapshot(): boolean {
  return query()?.matches === true;
}

function subscribe(l: () => void): () => void {
  const mq = query();
  if (!mq) return () => undefined;
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', l);
    return () => mq.removeEventListener('change', l);
  }
  mq.addListener?.(l);
  return () => mq.removeListener?.(l);
}

export function useSideways(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}
